import { CheckOutlined, SearchOutlined } from '@ant-design/icons';
import { ScopeButton } from '@monorepo/shared-lib';
import { Spin, Tooltip } from 'antd';
import { ScopeCheckbox } from 'components/Custom/checkbox';
import { RoundInput } from 'components/Custom/inputs';
import { LabelWrapper } from 'components/Custom/labels';
import { FormSelect } from 'components/Custom/selects';
import { MessageStates } from 'containers/MessageBox';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useCountryLabel } from 'hooks/useCountryLabel';
import { useDispatch, useSelector } from 'react-redux';
import { turnMessageOn } from 'store/app/actions';
import { getCommonCountries } from 'store/common/selectors';
import {
  getOrgLoading,
  getOrgOrganisations,
} from 'store/organisation/selectors';
import { getReturnTypesData } from 'store/returns/selectors';
import {
  fetchTaxRatesByCountryRequest,
  saveTaxRatesRequest,
} from 'store/tax-rates/actions';
import {
  getTaxRateTemplates,
  getTaxRatesByCountry,
  getTaxRatesLoading,
} from 'store/tax-rates/selectors';
import { usePwaMobileNavEnabled } from 'utils/usePwaMobileNavEnabled';
import styled, { createGlobalStyle } from 'styled-components';

const Wrapper = styled.div<{ pwaMobile?: boolean }>`
  background: var(--color-layout-bg, #e9f6f7);
  font-family: Inter, sans-serif;
  //border-radius: 8px;
  display: flex;
  flex-direction: column;
  justify-content: ${({ pwaMobile }) => (pwaMobile ? 'flex-start' : 'space-between')};
  height: ${({ pwaMobile }) =>
    pwaMobile ? '100%' : 'calc(95vh - 110px)'};
  min-height: ${({ pwaMobile }) => (pwaMobile ? '0' : 'auto')};
  flex: ${({ pwaMobile }) => (pwaMobile ? '1 1 auto' : '0 1 auto')};
  .divider-or {
    display: flex;
    align-items: center;
    margin: 8px 0 16px 0;
    color: var(--color-text-muted, #5a6a6a);
    font-size: 15px;
    font-weight: 500;
  }
  .divider-or:before,
  .divider-or:after {
    content: '';
    flex: 1;
    border-bottom: 1px solid var(--color-border, #dbeae3);
    margin: 0 12px;
  }
  .tax-rates-section {
    background-color: var(--color-surface, #f8fbfb);
    border-left: 3px solid var(--color-primary, #41aaaf);
    margin: 8px 0;
    padding: 12px 16px;
    border-radius: 0 4px 4px 0;
    min-height: 60px;
  }
  .tax-rates-title {
    font-family: Inter;
    font-weight: 600;
    font-size: 14px;
    color: var(--color-text-dark, #042a2a);
    margin-bottom: 12px;
  }
  .tax-rates-list {
    list-style: none;
    padding: 0;
    margin: 0;
  }
  .tax-rate-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 6px 0;
    border-bottom: 1px solid #e8f0f0;
  }
  .tax-rate-item:last-child {
    border-bottom: none;
  }
  .tax-rate-name {
    font-family: Inter;
    font-weight: 500;
    font-size: 13px;
    color: var(--color-text-dark, #042a2a);
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    margin-right: 12px;
  }
  .tax-rate-value {
    font-family: Inter;
    font-weight: 600;
    font-size: 13px;
    color: var(--color-primary, #41aaaf);
    flex-shrink: 0;
  }
  .no-tax-rates {
    font-family: Inter;
    font-weight: 400;
    font-size: 13px;
    color: var(--color-text-muted, #5a6a6a);
    font-style: italic;
  }
`;

const TaxRateListWrapper = styled.div<{ deduct: number; pwaMobile?: boolean }>`
  max-height: ${({ deduct, pwaMobile }) =>
    pwaMobile ? 'none' : `calc(95vh - ${deduct}px)`};
  min-height: 45px;
  overflow-y: ${({ pwaMobile }) => (pwaMobile ? 'visible' : 'auto')};
  background: var(--color-surface-raised, #fff);
  border: 1px solid var(--color-border, #dbeae3);
  padding: 0 8px 12px 8px;
  margin-bottom: 15px;
  /* Thinner, modern scrollbar */
  &::-webkit-scrollbar {
    width: 6px;
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: var(--color-border, #dbeae3);
    border-radius: 4px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
`;
const TaxRateItem = styled.label`
  display: flex;
  align-items: center;
  font-size: 12px;
  margin-bottom: 8px;
  cursor: pointer;
  user-select: none;
  &:last-child {
    margin-bottom: 0;
  }
`;
const TaxRateText = styled.span`
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
const SectionHeader = styled.div`
  font-family: Inter, sans-serif;
  font-weight: 600;
  font-size: 13px;
  color: var(--color-text-dark, #042a2a);
  margin-top: 12px;
  margin-bottom: 8px;
  &:first-child {
    margin-top: 0;
  }
`;
const MasterCheckboxWrapper = styled.div`
  display: flex;
  align-items: center;
  position: sticky;
  top: 0;
  background: var(--color-surface-raised, #fff);
  z-index: 2;
  padding-top: 4px;
  padding-bottom: 4px;
  border-bottom: 1px solid var(--color-border, #dbeae3);
  box-shadow: 0 2px 0 0 #fff;
`;

// Reduce margin below LabelWrapper and dropdowns
const labelStyle = { marginBottom: 9 };
const dropdownStyle = { marginBottom: 12 };
const countryDropdownStyle = { marginBottom: 32 };

// Add this after styled-components and before the component definition
const CustomNoticeLinkStyle = createGlobalStyle`
  .custom-notice-link {
    color: var(--color-primary, #41aaaf);
    font-size: 13px;
    font-family: Inter, sans-serif;
    font-weight: 700;
    padding: 0;
    cursor: pointer;
    text-decoration: none;
    transition: text-decoration 0.2s;
  }
  .custom-notice-link:hover, .custom-notice-link:focus {
    text-decoration: underline !important;
    color: var(--color-primary, #41aaaf) !important;
  }
  /* Only remove bottom padding when this specific form is in the drawer */
  .ant-drawer-body:has(.add-new-taxes-form-wrapper) {
    padding-bottom: 0 !important;
  }
`;

interface AddNewTaxesFormProps {
  loading?: boolean;
  connectionId: string;
  /** When set, the country dropdown is pre-selected to this country (e.g. entity's country). */
  initialCountryId?: string;
}

// Helper component to show tooltip only if text is truncated
const TaxRateEllipsisWithTooltip: React.FC<{
  children: React.ReactNode;
  title: string;
}> = ({ children, title }) => {
  const textRef = React.useRef<HTMLSpanElement>(null);
  const [isTruncated, setIsTruncated] = React.useState(false);

  React.useEffect(() => {
    const el = textRef.current;
    if (el) {
      setIsTruncated(el.scrollWidth > el.clientWidth);
    }
  }, [children, title]);

  const content = <TaxRateText ref={textRef}>{children}</TaxRateText>;
  return isTruncated ? (
    <Tooltip title={title} placement="left">
      {content}
    </Tooltip>
  ) : (
    content
  );
};

export const AddNewTaxesForm: React.FC<AddNewTaxesFormProps> = ({
  loading,
  connectionId,
  initialCountryId,
}) => {
  const { t } = useTranslation();
  const { localizeCountry } = useCountryLabel();
  const pwaMobileNav = usePwaMobileNavEnabled();
  const dispatch = useDispatch();
  const templates = useSelector(getTaxRateTemplates) || [];
  const countries = useSelector(getCommonCountries) || [];
  const taxRatesByCountry = useSelector(getTaxRatesByCountry);
  const taxRatesLoading = useSelector(getTaxRatesLoading);
  const returnTypesData = useSelector(getReturnTypesData);
  const orgLoading = useSelector(getOrgLoading);
  const organisations = useSelector(getOrgOrganisations) || [];

  const [selectedTemplateId, setSelectedTemplateId] = React.useState<
    string | undefined
  >(undefined);
  const [selectedCountryId, setSelectedCountryId] = React.useState<
    string | undefined
  >(initialCountryId ?? undefined);
  const [selectedTaxRateIds, setSelectedTaxRateIds] = React.useState<string[]>(
    []
  );
  const [hasReadNotice, setHasReadNotice] = React.useState(false);
  const [taxRateSearchTerm, setTaxRateSearchTerm] = React.useState('');

  const organisationTaxRates = React.useMemo(() => {
    if (!connectionId) return undefined;
    const org = (organisations as any[]).find(
      (o) => o && o.connectionId === connectionId
    );
    if (!org) return undefined;
    if (Array.isArray(org.taxRatesUnique) && org.taxRatesUnique.length > 0) {
      return org.taxRatesUnique;
    }
    return org.taxRates;
  }, [organisations, connectionId]);

  const cleanCategoryName = (input: string | undefined | null): string => {
    const base = (input ?? '').split(/@|\[|\(|\b\d+(\.\d+)?%/)[0];
    return base.trim();
  };

  const normaliseName = (name: string | undefined | null) =>
    cleanCategoryName(name)
      .toLowerCase()
      // Treat any non-alphanumeric (spaces, hyphens, punctuation, NBSP, etc.) as a space
      .replace(/[^a-z0-9]+/gi, ' ')
      .trim()
      .replace(/\s+/g, ' ');

  const getRateValue = (rate: any) => {
    const raw =
      rate && Object.prototype.hasOwnProperty.call(rate, 'taxRate')
        ? (rate as any).taxRate
        : (rate as any).rate;

    if (raw === null || raw === undefined) return '';

    const asNumber = Number(raw);
    if (!Number.isNaN(asNumber)) {
      // Normalise numeric values so 0, 0.0, "0.00" all become "0"
      return String(asNumber);
    }

    return String(raw).trim();
  };

  const isExistingRate = React.useCallback(
    (candidate: {
      id: string;
      name?: string;
      taxRate?: number;
      rate?: number;
    }) => {
      if (!Array.isArray(organisationTaxRates)) {
        // eslint-disable-next-line no-console
        //console.log('[AddNewTaxesForm] organisationTaxRates not array when checking rate', {
        //organisationTaxRates,
        //candidate,
        //});
        return false;
      }

      const candidateName = normaliseName(candidate.name);
      const candidateRate = getRateValue(candidate as any);

      const match = (organisationTaxRates as any[]).find((rate) => {
        const nameMatches = normaliseName(rate?.name) === candidateName;
        const valueMatches = getRateValue(rate) === candidateRate;
        return nameMatches && valueMatches;
      });

      if (!match) {
        // eslint-disable-next-line no-console
        //console.log(
        //'[AddNewTaxesForm] Rate treated as NEW (after direct scan)',
        //{
        //candidate,
        //candidateName,
        //candidateRate,
        //existing: organisationTaxRates,
        //existingNormalised: (organisationTaxRates as any[]).map((r) => ({
        //  id: r.id,
        //  name: r.name,
        //  normName: normaliseName(r.name),
        //  taxRate: (r as any).taxRate,
        //  value: getRateValue(r),
        //})),
        //});
      }

      return !!match;
    },
    [organisationTaxRates]
  );

  // Find selected template object
  const selectedTemplate = templates.find(
    (tpl) => tpl.id === selectedTemplateId
  );
  // Find selected country object
  const selectedCountry = countries.find((c) => c.id === selectedCountryId);

  // Filter countries based on returnTypesEdit data
  const filteredCountries = React.useMemo(() => {
    if (
      !returnTypesData?.returnTypesEdit ||
      returnTypesData.returnTypesEdit.length === 0
    ) {
      return countries.filter((c) => c.scopeSupported);
    }

    // Get unique country IDs from returnTypesEdit
    const availableCountryIds = new Set(
      returnTypesData.returnTypesEdit.map((returnType) => returnType.countryId)
    );

    // Filter countries that exist in returnTypesEdit and are scope supported
    return countries.filter(
      (c) => c.scopeSupported && availableCountryIds.has(c.id)
    );
  }, [countries, returnTypesData?.returnTypesEdit]);

  // Get tax rates for selected country from store
  const countryTaxRates = React.useMemo(() => {
    if (taxRatesByCountry && selectedCountryId) {
      return taxRatesByCountry.countryId === selectedCountryId
        ? taxRatesByCountry.taxRates
        : [];
    }
    return [];
  }, [taxRatesByCountry, selectedCountryId]);

  // Fetch tax rates when country changes
  React.useEffect(() => {
    if (selectedCountryId) {
      dispatch(fetchTaxRatesByCountryRequest(selectedCountryId));
    }
  }, [selectedCountryId, dispatch]);

  // Decide which tax rates to show
  const taxRatesToShow = selectedTemplate
    ? selectedTemplate.taxRates
    : selectedCountryId
    ? countryTaxRates
    : [];

  const recommendedTaxRates = React.useMemo(
    () => taxRatesToShow.filter((rate) => Boolean((rate as any).recommended)),
    [taxRatesToShow]
  );
  const otherTaxRates = React.useMemo(
    () => taxRatesToShow.filter((rate) => !Boolean((rate as any).recommended)),
    [taxRatesToShow]
  );

  // Find selected tax rate objects
  const selectedTaxRates = taxRatesToShow.filter((rate) =>
    selectedTaxRateIds.includes(rate.id)
  );

  const taxRateMatchesSearch = React.useCallback(
    (rate: { name?: string; taxRate?: number }) => {
      if (!taxRateSearchTerm.trim()) return true;
      const term = taxRateSearchTerm.trim().toLowerCase();
      const name = (rate.name ?? '').toLowerCase();
      const taxRateStr = getRateValue(rate as any).toLowerCase();
      return name.includes(term) || taxRateStr.includes(term);
    },
    [taxRateSearchTerm]
  );

  const filteredTaxRates = React.useMemo(
    () => taxRatesToShow.filter(taxRateMatchesSearch),
    [taxRatesToShow, taxRateMatchesSearch]
  );

  const filteredRecommendedTaxRates = React.useMemo(
    () => recommendedTaxRates.filter(taxRateMatchesSearch),
    [recommendedTaxRates, taxRateMatchesSearch]
  );
  const filteredOtherTaxRates = React.useMemo(
    () => otherTaxRates.filter(taxRateMatchesSearch),
    [otherTaxRates, taxRateMatchesSearch]
  );

  const selectableTaxRates = React.useMemo(
    () => taxRatesToShow.filter((rate) => !isExistingRate(rate as any)),
    [taxRatesToShow, isExistingRate]
  );

  // Master checkbox logic (works with filtered list)
  const filteredSelectableTaxRates = React.useMemo(
    () => filteredTaxRates.filter((rate) => !isExistingRate(rate as any)),
    [filteredTaxRates, isExistingRate]
  );

  const selectableRecommendedTaxRateIds = React.useMemo(
    () =>
      selectableTaxRates
        .filter((rate) => Boolean((rate as any).recommended))
        .map((rate) => rate.id),
    [selectableTaxRates]
  );

  React.useEffect(() => {
    if (selectableRecommendedTaxRateIds.length > 0) {
      setSelectedTaxRateIds((prev) => {
        const merged = new Set([...prev, ...selectableRecommendedTaxRateIds]);
        return Array.from(merged);
      });
    }
  }, [
    selectableRecommendedTaxRateIds.length > 0
      ? selectableRecommendedTaxRateIds.join(',')
      : '',
  ]);

  const visibleSelectedCount = filteredSelectableTaxRates.filter((rate) =>
    selectedTaxRateIds.includes(rate.id)
  ).length;
  const allChecked =
    filteredSelectableTaxRates.length > 0 &&
    visibleSelectedCount === filteredSelectableTaxRates.length;
  const isIndeterminate =
    visibleSelectedCount > 0 &&
    visibleSelectedCount < filteredSelectableTaxRates.length;

  return (
    <Spin
      spinning={!!loading || (!!selectedCountryId && !!orgLoading)}
      size="large"
    >
      <CustomNoticeLinkStyle />
      <Wrapper
        className={[
          'add-new-taxes-form-wrapper',
          pwaMobileNav ? 'scope-pwa-setup-picker-form' : undefined,
        ]
          .filter(Boolean)
          .join(' ')}
        pwaMobile={pwaMobileNav}
      >
        <div className="top-section">
          <LabelWrapper style={labelStyle}>
            <span className="label-text">{t('organisation.select-template')}</span>
          </LabelWrapper>
          <FormSelect
            placeholder={
              templates.length === 0
                ? t('organisation.templates-not-available')
                : t('organisation.select-template')
            }
            value={selectedTemplateId}
            onChange={(val) => {
              const nextTemplateId = val as string | undefined;
              setSelectedTemplateId(nextTemplateId);

              if (nextTemplateId) {
                // When a template is selected, clear country (template drives rates)
                setSelectedCountryId(undefined);
              } else if (initialCountryId) {
                // When template is cleared, fall back to default country
                setSelectedCountryId(initialCountryId);
              }

              setSelectedTaxRateIds([]);
              setTaxRateSearchTerm('');
            }}
            allowClear
            showSearch
            optionFilterProp="children"
            width="100%"
            height="45px"
            bordercolor="var(--color-border, #dbeae3)"
            bgcolor="var(--color-surface-raised, #FFFFFF)"
            style={{
              ...dropdownStyle,
              fontStyle: templates.length === 0 ? 'italic' : 'normal',
            }}
            disabled={templates.length === 0}
          >
            {templates.map((tpl) => (
              <FormSelect.Option key={tpl.id} value={tpl.id}>
                {tpl.name} ({tpl.taxRates?.length ?? 0})
              </FormSelect.Option>
            ))}
          </FormSelect>

          <div className="divider-or">{t('signup.OR')}</div>

          <LabelWrapper style={labelStyle}>
            <span className="label-text">{t('organisation.select-country')}</span>
          </LabelWrapper>
          <FormSelect
            placeholder={
              filteredCountries.length === 0
                ? t('organisation.countries-not-available')
                : t('organisation.select-country')
            }
            value={selectedCountryId}
            onChange={(val) => {
              setSelectedCountryId(val as string | undefined);
              setSelectedTemplateId(undefined);
              setSelectedTaxRateIds([]);
              setTaxRateSearchTerm('');
            }}
            allowClear
            showSearch
            optionFilterProp="children"
            width="100%"
            height="45px"
            bordercolor="var(--color-border, #dbeae3)"
            bgcolor="var(--color-surface-raised, #FFFFFF)"
            style={{
              ...countryDropdownStyle,
              fontStyle: filteredCountries.length === 0 ? 'italic' : 'normal',
            }}
            disabled={filteredCountries.length === 0}
          >
            {filteredCountries.map((country) => (
              <FormSelect.Option key={country.id} value={country.id}>
                {localizeCountry(country.iso, country.name)}
              </FormSelect.Option>
            ))}
          </FormSelect>

          <LabelWrapper style={labelStyle}>
            <span className="label-text">{t('organisation.select-tax-rates')}</span>
          </LabelWrapper>
          {taxRatesToShow.length > 0 && (
            <RoundInput
              placeholder={t('organisation.search-tax-rates')}
              prefix={<SearchOutlined style={{ color: 'var(--color-text-muted, #5a6a6a)' }} />}
              value={taxRateSearchTerm}
              onChange={(e) => setTaxRateSearchTerm(e.target.value)}
              allowClear
              bg_color="var(--color-surface-raised, #ffffff)"
              width="100%"
              height="36px"
              style={{
                marginBottom: 8,
                fontSize: 12,
              }}
            />
          )}
          <TaxRateListWrapper
            deduct={selectedTemplate ? 420 : selectedCountryId ? 450 : 420}
            pwaMobile={pwaMobileNav}
          >
            {taxRatesToShow.length > 0 && (
              <MasterCheckboxWrapper>
                <ScopeCheckbox
                  compact
                  aria-label={t('common.select-all')}
                  indeterminate={isIndeterminate}
                  checked={allChecked}
                  onChange={(e) => {
                    if (e.target.checked) {
                      const idsToAdd = filteredSelectableTaxRates.map(
                        (rate) => rate.id
                      );
                      setSelectedTaxRateIds((prev) =>
                        Array.from(new Set([...prev, ...idsToAdd]))
                      );
                    } else {
                      const idsToRemove = new Set(
                        filteredSelectableTaxRates.map((rate) => rate.id)
                      );
                      setSelectedTaxRateIds((prev) =>
                        prev.filter((id) => !idsToRemove.has(id))
                      );
                    }
                  }}
                  style={{ marginRight: 8 }}
                />
                <span
                  style={{
                    fontWeight: 400,
                    fontSize: 11,
                    color: 'var(--color-placeholder, #9ca7b0)',
                    fontFamily: 'Inter, sans-serif',
                    fontStyle: 'italic',
                  }}
                >
                  {t('common.select-all')}
                </span>
              </MasterCheckboxWrapper>
            )}
            <div style={{ paddingTop: 12 }}>
              {taxRatesToShow.length === 0 ? (
                <div className="no-tax-rates">{t('organisation.no-tax-rates-available')}</div>
              ) : filteredTaxRates.length === 0 ? (
                <div className="no-tax-rates">
                  {t('organisation.no-tax-rates-match-search')}
                </div>
              ) : selectedTemplate ? (
                filteredTaxRates.map((rate) => {
                  const existing = isExistingRate(rate as any);

                  return (
                    <TaxRateItem
                      key={rate.id}
                      style={existing ? { fontStyle: 'italic' } : undefined}
                    >
                      {existing ? (
                        <Tooltip title={t('organisation.already-in-your-tax-rates')}>
                          <CheckOutlined
                            style={{
                              marginRight: 8,
                              color: 'var(--color-primary, #41aaaf)',
                              fontSize: 13,
                            }}
                          />
                        </Tooltip>
                      ) : (
                        <ScopeCheckbox
                          compact
                          aria-label={rate.name}
                          checked={selectedTaxRateIds.includes(rate.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedTaxRateIds([
                                ...selectedTaxRateIds,
                                rate.id,
                              ]);
                            } else {
                              setSelectedTaxRateIds(
                                selectedTaxRateIds.filter(
                                  (id) => id !== rate.id
                                )
                              );
                            }
                          }}
                          style={{ marginRight: 8 }}
                        />
                      )}
                      <TaxRateEllipsisWithTooltip title={rate.name}>
                        {rate.name}
                      </TaxRateEllipsisWithTooltip>
                    </TaxRateItem>
                  );
                })
              ) : (
                <>
                  {filteredRecommendedTaxRates.length > 0 && (
                    <>
                      <SectionHeader>{t('organisation.recommended')}</SectionHeader>
                      {filteredRecommendedTaxRates.map((rate) => {
                        const existing = isExistingRate(rate as any);

                        return (
                          <TaxRateItem
                            key={rate.id}
                            style={
                              existing ? { fontStyle: 'italic' } : undefined
                            }
                          >
                            {existing ? (
                              <Tooltip title={t('organisation.already-in-your-tax-rates')}>
                                <CheckOutlined
                                  style={{
                                    marginRight: 8,
                                    color: 'var(--color-primary, #41aaaf)',
                                    fontSize: 13,
                                  }}
                                />
                              </Tooltip>
                            ) : (
                              <ScopeCheckbox
                                compact
                                aria-label={rate.name}
                                checked={selectedTaxRateIds.includes(rate.id)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedTaxRateIds([
                                      ...selectedTaxRateIds,
                                      rate.id,
                                    ]);
                                  } else {
                                    setSelectedTaxRateIds(
                                      selectedTaxRateIds.filter(
                                        (id) => id !== rate.id
                                      )
                                    );
                                  }
                                }}
                                style={{ marginRight: 8 }}
                              />
                            )}
                            <TaxRateEllipsisWithTooltip title={rate.name}>
                              {rate.name}
                            </TaxRateEllipsisWithTooltip>
                          </TaxRateItem>
                        );
                      })}
                    </>
                  )}
                  {filteredOtherTaxRates.length > 0 && (
                    <>
                      <SectionHeader>{t('organisation.other-tax-rates')}</SectionHeader>
                      {filteredOtherTaxRates.map((rate) => {
                        const existing = isExistingRate(rate as any);

                        return (
                          <TaxRateItem
                            key={rate.id}
                            style={
                              existing ? { fontStyle: 'italic' } : undefined
                            }
                          >
                            {existing ? (
                              <Tooltip title={t('organisation.already-in-your-tax-rates')}>
                                <CheckOutlined
                                  style={{
                                    marginRight: 8,
                                    color: 'var(--color-primary, #41aaaf)',
                                    fontSize: 13,
                                  }}
                                />
                              </Tooltip>
                            ) : (
                              <ScopeCheckbox
                                compact
                                aria-label={rate.name}
                                checked={selectedTaxRateIds.includes(rate.id)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedTaxRateIds([
                                      ...selectedTaxRateIds,
                                      rate.id,
                                    ]);
                                  } else {
                                    setSelectedTaxRateIds(
                                      selectedTaxRateIds.filter(
                                        (id) => id !== rate.id
                                      )
                                    );
                                  }
                                }}
                                style={{ marginRight: 8 }}
                              />
                            )}
                            <TaxRateEllipsisWithTooltip title={rate.name}>
                              {rate.name}
                            </TaxRateEllipsisWithTooltip>
                          </TaxRateItem>
                        );
                      })}
                    </>
                  )}
                </>
              )}
            </div>
          </TaxRateListWrapper>
        </div>
        <div className="bottom-section">
          {/* Checkbox for reading the notice and Open notice link - only show if using country */}
          {selectedCountryId && !selectedTemplateId && (
            <div
              style={{
                marginBottom: 16,
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <ScopeCheckbox
                id="read-notice-checkbox"
                checked={hasReadNotice}
                onChange={(e) => setHasReadNotice(e.target.checked)}
                style={{ marginRight: 8 }}
              />
              <label
                htmlFor="read-notice-checkbox"
                className="disclaimer-label"
                style={{
                  cursor: 'pointer',
                  fontWeight: 700,
                  fontSize: '13px',
                  color: 'var(--color-text-dark, #042a2a)',
                  fontFamily: 'Inter, sans-serif',
                  marginRight: 8,
                }}
              >
                {t('organisation.i-have-read-the-notice')}
              </label>
              <a
                href="#"
                className="custom-notice-link"
                onClick={(e) => {
                  e.preventDefault();
                  dispatch(
                    turnMessageOn({
                      title: t('organisation.notice'),
                      description: (
                        <span>
                          {t('organisation.tax-rates-notice-intro')}
                          <ul
                            style={{
                              margin: 0,
                              paddingLeft: 18,
                              listStyleType: 'disc',
                            }}
                          >
                            <li>
                              {t('organisation.tax-rates-notice-item-guideline')}
                            </li>
                            <li>{t('organisation.tax-rates-notice-item-oss')}</li>
                            <li>
                              {t(
                                'organisation.tax-rates-notice-item-reverse-charge'
                              )}
                            </li>
                          </ul>
                        </span>
                      ),
                      type: MessageStates.INFO,
                      duration: 15,
                    })
                  );
                }}
                tabIndex={0}
              >
                {t('organisation.open-notice')}
              </a>
            </div>
          )}

          <ScopeButton
            type="primary"
            width="100%"
            height="45px"
            loading={taxRatesLoading}
            disabled={
              taxRatesLoading ||
              selectedTaxRateIds.length === 0 ||
              (!!selectedCountryId && !selectedTemplateId && !hasReadNotice)
            }
            onClick={() => {
              if (
                !taxRatesLoading &&
                selectedTaxRateIds.length > 0 &&
                (!selectedCountryId || selectedTemplateId || hasReadNotice)
              ) {
                dispatch(saveTaxRatesRequest(connectionId, selectedTaxRateIds));
              }
            }}
          >
            {selectedTaxRateIds.length === 0
              ? t('organisation.add-tax-rates')
              : t('organisation.add-tax-rates-count', {
                  count: selectedTaxRateIds.length,
                })}
          </ScopeButton>
        </div>
      </Wrapper>
    </Spin>
  );
};
